// xadomTableSelectors.ts
import { createSelector } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';

import { RootState } from '../../../../app/appStore';

import { rowsAdapter } from './row/logic';
import { Column, Filter, FilterOperator, Row, SummaryColumn, ValueType } from './types';

// Selectors
const selectTableRowState = (state: RootState) => state.xadomTableRow;
const selectTableState = (state: RootState) => state.xadomTable.tables;


// 테이블 상태 선택
export const makeSelectTableStateByTableId = (tableId: string) =>
    createSelector(
        [selectTableState],
        (tables) => tables[tableId] || null,
        {
            memoizeOptions: {
                resultEqualityCheck: isEqual
            }
        }
    );

// 컬럼 상태 선택
export const makeSelectTableColumnsByTableId = (tableId: string) =>
    createSelector(
        [selectTableState],
        (tables) => tables[tableId]?.columns || {}
    );

export const makeSelectColumnsWithoutFiltersByTableId = (tableId: string) =>
    createSelector(
        [makeSelectTableColumnsByTableId(tableId)],
        (columns) => {
            // Only create a new object if columns have changed
            const columnsWithoutFilters = Object.fromEntries(
                Object.entries(columns).map(([columnId, column]) => {
                    const { filter, ...rest } = column;
                    return [columnId, rest];
                })
            );
            return columnsWithoutFilters;
        }
    );


// 컬럼 필터 상태 선택
export const makeSelectColumnFilterByTableId = (tableId: string) =>
    createSelector(
        [makeSelectTableColumnsByTableId(tableId)],
        (columns) => {
            const filters = [];
            for (const [columnId, column] of Object.entries(columns)) {
                if (column.filter) {
                    filters.push({ columnId, filter: column.filter });
                }
            }
            return filters;
        }
    );

// EntityState<Row, string> 선택, 단, 필더링을 적용함
export const makeSelectTableRowsByTableId = (tableId: string) =>
    createSelector([selectTableRowState], (tableRowState) => {
        return tableRowState[tableId];
    });

// Row[] 선택
export const makeSelectAllRowsByTableId = (tableId: string) =>
    createSelector(
        [makeSelectTableRowsByTableId(tableId)],
        (rows) => rowsAdapter.getSelectors().selectAll(rows)
    );

// 테이블의 총 행 수를 반환하는 선택자
// undefined인 경우 0을 반환하여 안전성 보장
export const makeSelectRowCountByTableId = (tableId: string) =>
    createSelector(
        [makeSelectTableRowsByTableId(tableId)],
        (rows) => rows ? rowsAdapter.getSelectors().selectTotal(rows) : 0
    );

// RowIndex로 로우 선택
export const makeSelectRowByRowIndex = (tableId: string, rowIndex: number) =>
    createSelector(
        [makeSelectFilteredRowsByTableId(tableId)],
        (rows) => rows[rowIndex]
    );

// RowId로 로우 선택
export const makeSelectRowById = (tableId: string, rowId: string) =>
    createSelector(
        [makeSelectTableRowsByTableId(tableId)],
        (tableRows) => rowsAdapter.getSelectors().selectById(tableRows, rowId)
    );


// 필터 상태 선택
export const makeSelectFiltersByTableId = (tableId: string) =>
    createSelector(
        [(state: RootState) => state.xadomTable.tables[tableId]?.columns || {}],
        (columns) => {
            const filters = Object.entries(columns)
                .filter(([_, column]) => column.filter !== undefined)
                .map(([columnId, column]) => ({
                    columnId,
                    filter: column.filter!
                }));
            return filters;
        },
        {
            memoizeOptions: {
                resultEqualityCheck: isEqual
            }
        }
    );
// 필터링된 로우 선택자 캐시
const filteredRowsSelectorCache: Record<string, (state: RootState) => Row[]> = {};

// selectCellByRowIdAndColumnId
export const makeSelectCellByRowIdAndColumnId = (tableId: string, rowId: string, columnId: string) =>
    createSelector(
        [makeSelectFilteredRowsByTableId(tableId)],
        (rows) => rows.find(row => row.id === rowId)?.data[columnId]
    );

// 필터링된 로우 선택자 팩토리 (캐시 적용)
export const makeSelectFilteredRowsByTableId = (tableId: string) => {
    if (!filteredRowsSelectorCache[tableId]) {
        filteredRowsSelectorCache[tableId] = createSelector(
            [
                (state: RootState) => selectTableRowState(state)[tableId],
                makeSelectFiltersByTableId(tableId)
            ],
            (tableRowsState, filters) => {
                if (!tableRowsState) return [];
                const allRows = rowsAdapter.getSelectors().selectAll(tableRowsState);
                return applyFiltersToRows(allRows, filters);
            },
            {
                memoizeOptions: {
                    resultEqualityCheck: isEqual
                }
            }
        );
    }
    return filteredRowsSelectorCache[tableId];
};

// 특정 열 합계 계산을 위한 선택자
export const makeSelectSumByColumn = (tableId: string, columnId: string) =>
    createSelector(
        [makeSelectFilteredRowsByTableId(tableId)],
        (rows): number => {
            let sum = 0;
            for (let i = 0; i < rows.length; i++) {
                const cell = rows[i].data[columnId];
                if (!cell || cell.value === undefined || cell.value === null) {
                    continue;
                }
                const cellValue = parseFloat(cell.value);
                if (!isNaN(cellValue)) {
                    sum += cellValue;
                }
            }
            return sum;
        },
        {
            memoizeOptions: {
                resultEqualityCheck: (a: number, b: number) =>
                    Math.abs(a - b) < Number.EPSILON
            }
        }
    );


// Helper function to apply filters to rows
function applyFiltersToRows(rows: Row[], filters: Array<{ columnId: string; filter: Filter }>): Row[] {
    if (!filters || filters.length === 0) {
        return rows;
    }

    const filterdRows = rows.filter((row) => {
        return filters.some(({ columnId, filter }) => {
            const cell = row.data[columnId];
            const cellValue = cell?.value;
            return applyFilterToValue(cellValue, filter);
        });
    });

    return filterdRows;
}

// Helper function to apply a single filter to a cell value
function applyFilterToValue(value: any, filter: Filter): boolean {
    if (!filter || !filter.type || filter.value === undefined || filter.value === null) {
        return true; // No filter applied
    }

    switch (filter.type) {
        case ValueType.STRING:
            const stringValue = value?.toString() || '';
            return applyTextFilter(stringValue, filter);
        case ValueType.NUMBER:
            if (typeof value !== 'number') {
                return false;
            }
            return applyNumberFilter(value, filter);
        case ValueType.DATE:
            const dateValue = new Date(value);
            if (isNaN(dateValue.getTime())) {
                return false;
            }
            return applyDateFilter(dateValue, filter);
        case ValueType.SELECT:
            return applySelectFilter(value, filter);
        default:
            return true;
    }
}

// Function to apply text filters
function applyTextFilter(value: string, filter: Filter): boolean {
    const filterValue = filter.value?.toString() || '';

    switch (filter.operator) {
        case FilterOperator.EQUALS:
            return value === filterValue;
        case FilterOperator.CONTAINS:
            return value.includes(filterValue);
        case FilterOperator.STARTS_WITH:
            return value.startsWith(filterValue);
        case FilterOperator.ENDS_WITH:
            return value.endsWith(filterValue);
        default:
            return true;
    }
}

// Function to apply number filters
function applyNumberFilter(value: number, filter: Filter): boolean {
    const filterValue = filter.value as number;
    switch (filter.operator) {
        case 'equals':
            return value === filterValue;
        case 'greaterThan':
            return value > filterValue;
        case 'lessThan':
            return value < filterValue;
        default:
            return true;
    }
}

// Function to apply date filters
function applyDateFilter(value: Date, filter: Filter): boolean {
    const filterValue = new Date(filter.value as Date);
    switch (filter.operator) {
        case 'equals':
            return value.getTime() === filterValue.getTime();
        case 'greaterThan':
            return value > filterValue;
        case 'lessThan':
            return value < filterValue;
        default:
            return true;
    }
}

// Function to apply select filters
function applySelectFilter(value: any, filter: Filter): boolean {
    const options = filter.options;
    if (!options) {
        return true;
    }
    return options.includes(value);
}

// 셀렉터 추가
export const selectFlattenedColumns = (state: RootState, tableId: string): Column[] => {
    const table = state.xadomTable.tables[tableId];
    if (!table) return [];

    const columns = Object.values(table.columns);
    const flattenedColumns: Column[] = [];

    const flattenColumn = (column: Column) => {
        if (column.children && column.children.length > 0) {
            // 자식 컬럼들을 재귀적으로 처리
            column.children.forEach(childColumn => flattenColumn(childColumn));
        } else {
            // 자식이 없는 컬럼만 추가
            flattenedColumns.push(column);
        }
    };

    columns.forEach(flattenColumn);
    return flattenedColumns;
};

//GROUP이 아닌 SummaryColumn만 선택
export const makeSelectNonGroupSummaryColumns = (tableId: string) =>
    createSelector(
        [makeSelectTableColumnsByTableId(tableId)],
        (columns) => {
            const nonGroupColumns: SummaryColumn[] = [];

            const processColumn = (column: SummaryColumn) => {
                if (column.type !== ValueType.GROUP) {
                    nonGroupColumns.push(column);
                } else if (column.children) {
                    // 그룹 컬럼의 자식들을 재귀적으로 처리
                    column.children.forEach(processColumn);
                }
            };

            Object.values(columns).forEach(processColumn);
            return nonGroupColumns;
        }
    );

// GROUP이 아닌 컬럼만 선택 (재귀적으로 처리)
export const makeSelectNonGroupColumns = (tableId: string) =>
    createSelector(
        [makeSelectTableColumnsByTableId(tableId)],
        (columns) => {
            const nonGroupColumns: Column[] = [];

            const processColumn = (column: Column) => {
                if (column.type !== ValueType.GROUP) {
                    nonGroupColumns.push(column);
                } else if (column.children) {
                    // 그룹 컬럼의 자식들을 재귀적으로 처리
                    column.children.forEach(processColumn);
                }
            };

            Object.values(columns).forEach(processColumn);
            return nonGroupColumns;
        }
    );

// 컬럼 갯수를 세는 셀렉터 (GROUP이 아닌 컬럼만 세기, GROUP이 소유한 children 이 GROUP일 경우가 있으니 재귀적으로 처리)
export const makeSelectColumnCount = (tableId: string) =>
    createSelector(
        [makeSelectTableColumnsByTableId(tableId)],
        (columns) => countColumns(columns)
    );

function countColumns(columns: { [key: string]: Column }): number {
    return Object.values(columns).reduce((count, column) => {
        return count + (column.type !== ValueType.GROUP ? 1 : 0);
    }, 0);
}



// 컬럼 옵션 선택자
export const makeSelectColumnOptions = (tableId: string, columnId: string) =>
    createSelector(
        [makeSelectNonGroupColumns(tableId)],
        (columns) => {
            const column = columns.find(col => col.id === columnId);
            return column?.type === ValueType.SELECT ? column.options : [];
        }
    );

// rowIndex와 columnIndex로 셀 선택자
export const makeSelectCellByIndices = (tableId: string, rowIndex: number, columnIndex: number) =>
    createSelector(
        [
            makeSelectFilteredRowsByTableId(tableId),
            (state: RootState) => selectFlattenedColumns(state, tableId)
        ],
        (rows, columns) => {
            if (!rows || !columns || rowIndex >= rows.length || columnIndex >= columns.length) {
                return null;
            }
            const row = rows[rowIndex];
            const column = columns[columnIndex];
            return row.data[column.id];
        }
    );

export const makeSelectCheckedRows = (tableId: string, columnId: string) =>
    createSelector(
        [
            makeSelectFilteredRowsByTableId(tableId)
        ],
        (rows) => rows.filter(row => row.data[columnId]?.value === true)
    );

export const selectAllRowsChecked = (tableId: string, columnId: string) =>
    (state: RootState): boolean => {
        const rows = makeSelectFilteredRowsByTableId(tableId)(state);
        if (!rows || rows.length === 0) return false;

        // readOnly가 아니고 새로운 행이 아닌 행들만 필터링하여 체크 상태 확인
        const nonDisabledRows = rows.filter(row => !row.data.isNewRow?.value && !row.data[columnId]?.readOnly);
        if (nonDisabledRows.length === 0) return false;

        return nonDisabledRows.every(row => row.data[columnId]?.value === true);
    };

export const selectRowFilter = (tableId: string) =>
    createSelector(
        [makeSelectTableStateByTableId(tableId)],
        (tableState) => tableState.rowFilter
    );

// 선택된 셀 정보를 가져오는 selector
export const makeSelectSelectedCellByTableId = (tableId: string) =>
    createSelector(
        [makeSelectTableStateByTableId(tableId)],
        (tableState) => tableState?.selectedCell || { rowIndex: -1, columnIndex: -1 }
    );

// 선택된 행 정보를 가져오는 selector
export const makeSelectSelectedRowByTableId = (tableId: string) =>
    createSelector(
        [
            makeSelectFilteredRowsByTableId(tableId),
            makeSelectSelectedCellByTableId(tableId)
        ],
        (rows, selectedCell) => {
            if (!rows || selectedCell.rowIndex < 0 || selectedCell.rowIndex >= rows.length) {
                return null;
            }
            return rows[selectedCell.rowIndex];
        }
    );

// 선택된 셀의 값을 가져오는 selector
export const makeSelectSelectedCellValueByTableId = (tableId: string) =>
    createSelector(
        [
            makeSelectFilteredRowsByTableId(tableId),
            makeSelectSelectedCellByTableId(tableId),
            (state: RootState) => selectFlattenedColumns(state, tableId)
        ],
        (rows, selectedCell, columns) => {
            if (!rows || !columns || 
                selectedCell.rowIndex < 0 || selectedCell.rowIndex >= rows.length ||
                selectedCell.columnIndex < 0 || selectedCell.columnIndex >= columns.length) {
                return null;
            }
            const row = rows[selectedCell.rowIndex];
            const column = columns[selectedCell.columnIndex];
            return row.data[column.id];
        }
    );


